/* eslint-disable @typescript-eslint/no-explicit-any */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'

import { createEventLog, updateParticipant } from 'api/mutations'
import { FORM_ORDER_OMIT_FIELDS } from 'constants/api'
import { formatDate } from 'constants/formats'
import { EVENT_LOG_ACTION_TYPES } from 'constants/models'
import { Event, LoggedInUser, Participant, Toast } from 'models/API'
import { doMutation } from 'utils/ApiUtils'

import Details from './Details'
import Form from './form'
import schema from './form/schema'

interface Props {
  data: Participant
  event: Event
  index: number
  loggedInUser: LoggedInUser
  toast: (toast: Toast) => void
}

const PARTICIPANT_FIELDS = [
  'bibName',
  'bibNumber',
  'firstName',
  'lastName',
  'emergencyContactName',
  'emergencyContactPhone',
  'teamName',
  'categoryId',
  'subCategory',
  'shirtSize',
  'shirtType',
  'shirtSize2',
  'shirtType2',
  'jerseySize',
  'birthdate',
  'gender',
  'nationality',
  'email',
  'phoneNumber',
  'address',
  'tandemName',
  'tandemBirthdate',
  'tandemShirtSize',
  'additionalId',
  'firstParticipantInfo',
  'relayEvent',
  'mandatoryCheck',
]

const DATE_FORMAT = 'YYYY-MM-DD'
const getDifference = (objA: any, objB: any) => {
  const diff = { oldData: {}, newData: {} }

  // Find keys in A that are different in B or missing in B
  Object.keys(objA)
    .filter(
      (key) =>
        PARTICIPANT_FIELDS.includes(key) &&
        !FORM_ORDER_OMIT_FIELDS.includes(key),
    )
    .forEach((key) => {
      if (['birthdate', 'tandemBirthdate'].includes(key)) {
        const dateA =
          typeof objA[key] === 'string'
            ? objA[key]
            : formatDate(objA[key], DATE_FORMAT)
        const dateB =
          typeof objB[key] === 'string'
            ? objB[key]
            : formatDate(objB[key], DATE_FORMAT)
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          dateA !== dateB
        ) {
          diff.oldData[key] = dateA
          diff.newData[key] = dateB
        }
      } else if (['relayEvent'].includes(key)) {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key] ?? '') !== objB[key].join(',')
        ) {
          diff.oldData[key] = objA[key]
          diff.newData[key] =
            objB[key].join(',').length > 0 ? objB[key].join(',') : null
        }
      } else {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key] ?? '') !== (objB[key] ?? '')
        ) {
          diff.oldData[key] = objA[key]
          diff.newData[key] = objB[key]
        }
      }
    })

  // Find keys in B that are missing in A (the 'd: 5' case)
  Object.keys(objB)
    .filter(
      (key) =>
        PARTICIPANT_FIELDS.includes(key) &&
        !FORM_ORDER_OMIT_FIELDS.includes(key),
    )
    .forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(objA, key)) {
        diff.oldData[key] = objA[key]
        diff.newData[key] = objB[key]
      }
    })

  console.log({ diff })

  return diff
}

const Item: React.FC<Props> = ({
  data,
  event,
  index,
  loggedInUser,
  toast,
}: Props) => {
  const [participant, setParticipant] = useState(data)
  const [edit, setEdit] = useState(false)

  const handleEditClick = useCallback(() => {
    setEdit(!edit)
  }, [edit])

  const handleSubmit = useCallback(
    async (values: Yup.InferType<typeof schema>) => {
      const diff = getDifference(participant, values)

      const eventLog = {
        id: uuidv4(),
        eventId: event.id,
        staffId: loggedInUser.sub,
        action: EVENT_LOG_ACTION_TYPES.UPDATE,
        newData: JSON.stringify(diff.newData),
        oldData: JSON.stringify(diff.oldData),
      }

      try {
        await doMutation('updateParticipant', updateParticipant, {
          input: {
            id: participant.id,
            ...diff.newData,
          },
        })

        await doMutation('createEventLog', createEventLog, {
          input: eventLog,
        })
        setParticipant({
          ...participant,
          ...diff.newData,
        })
        setEdit(false)
        toast({
          title: 'Success',
          message: 'Participant details updated successfully',
        })
      } catch (e) {
        console.log(e)
      }
    },
    [participant],
  )
  console.log({ participant })

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}>
        <Typography component="span">
          {data.firstName} {data.lastName}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {edit ? (
          <Form
            onSubmit={handleSubmit}
            onCancel={handleEditClick}
            participant={participant}
            event={event}
          />
        ) : (
          <Details
            participant={participant}
            event={event}
            onClick={handleEditClick}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default Item
